import { ExpandedSurvey, NotificationGroup, Timezone, TrackedLink } from "@aidkitorg/types/lib/survey";
import { useState, useEffect, useCallback, useMemo } from "react";
import { usePost } from "../API";
import { DistroExprLink } from "../Components/ExploreLink";
import { Alert, Button, DateTimeInput, getBrowserTimezone } from "@aidkitorg/component-library";
import { SqlViewer, NotificationSimulation, RecipientsTable } from "./CommonComponents";
import { EnableKey } from "./EnableKey";
import { useDebouncedCallback } from "../Hooks/Debounce";
import * as v0 from "@aidkitorg/types/lib/survey";
import { HasEnableKey } from "@aidkitorg/types/lib/eval";
import type { NotificationRecipientsResult } from "@aidkitorg/aidkit/lib/notifications/NotificationService";
import { NotificationRecipientsDownloadModal } from "../AdminPanel/Notifications/components/NotificationRecipientsDownloadModal";

export function Followup(props: {
  task: NonNullable<ExpandedSurvey['notifications']>[number],
  flup: Exclude<NonNullable<NotificationGroup>['followups'], undefined>[number],
  enableEmailMarkdownGlobally: boolean
}) {
  let simulateNotifications = usePost('/program/admin/simulate_notifications');
  let getNotificationRecipients = usePost('/api/admin_panel/notifications/recipients');

  let [simulated, setSimulated] = useState<Awaited<ReturnType<typeof simulateNotifications>> | null>(null);
  let [liveUpdating, setLiveUpdating] = useState<null | "simulation" | "eligibility">(null);
  let [simulating, setSimulating] = useState(false);
  let [testContactMethod, setTestContactMethod] = useState<string>('sms');
  const [fullQueryResult, setFullQueryResult] = useState<NotificationRecipientsResult | null>(null);
  const [runningFullQuery, setRunningFullQuery] = useState(false);
  const [fullQueryError, setFullQueryError] = useState<string | null>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [recipientsTab, setRecipientsTab] = useState<'sent' | 'eligible'>('eligible');
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | undefined>(() => new Date().toISOString());
  const [selectedTimezone, setSelectedTimezone] = useState<Timezone>(() => getBrowserTimezone());
  // Custom SQL runs verbatim, so its NOW() can't be simulated
  const isCustomSQL = props.flup.send_if.kind === 'SQL';
  const multipleFailed = fullQueryResult && fullQueryResult.uniqueFailedCount > 1;

  useEffect(() => {
    if (['email', 'sms', 'whatsapp'].includes(props.task.contactMethod)) {
      setTestContactMethod(props.task.contactMethod);
    }
  }, [props.task.contactMethod]);

  const runFullQuery = useCallback(async () => {
    setRunningFullQuery(true);
    setFullQueryError(null);
    setFullQueryResult(null);

    if (!props.task.recipient) {
      setFullQueryError('Cannot run full query for notifications without a recipient');
      setRunningFullQuery(false);
      return;
    }

    // Build a NotificationGroup that represents this followup
    const notificationGroup: v0.NotificationGroup = {
      kind: 'Notification',
      version: 1,
      name: props.task.name,
      targetPrefix: props.task.targetPrefix,
      recipient: props.task.recipient,
      contactMethod: props.task.contactMethod,
      initial_notification: props.task.initial_notification,
      followups: [props.flup], // Only include this followup
      enableKey: props.task.kind !== 'InlineNotification' ? props.task.enableKey : '',
    };

    const resp = await getNotificationRecipients({
      targetFieldPrefix: `${props.task.targetPrefix}_${props.flup.suffix}`,
      notificationGroup,
      simulationDate: isCustomSQL ? undefined : selectedTimestamp,
    });

    if (resp && resp.success && resp.data) {
      setFullQueryResult(resp.data);
    } else {
      setFullQueryError(resp?.message || 'Failed to run full query');
    }

    setRunningFullQuery(false);
  }, [props.task, props.flup, getNotificationRecipients, selectedTimestamp]);

  const debouncedRunFullQuery = useDebouncedCallback(() => runFullQuery(), 2000, [runFullQuery]);

  useEffect(() => {
    if (liveUpdating === "eligibility") {
      setRunningFullQuery(true);
      debouncedRunFullQuery();
    }
  }, [JSON.stringify(props), liveUpdating, selectedTimestamp]);

  const simulate = useCallback(async () => {
    setSimulating(true);
    setSimulated(await simulateNotifications({
      uids: props.task.testUIDs || [],
      simulateAllLangs: true,
      emailKey: typeof props.task.recipient === 'object' ? props.task.recipient.emailField : '',
      phoneKey: typeof props.task.recipient === 'object' ? props.task.recipient.phoneField : '',
      content: props.flup.message as Record<string, string>,
      emailContent: props.flup.email_message,
      enableEmailMarkdown: props.task.enableEmailMarkdown || props.enableEmailMarkdownGlobally,
      links: (props.flup.subsurveys || []).reduce((obj, curr) => {
        obj[curr.variable] = curr.name;
        return obj
      }, {} as Record<string, string | TrackedLink>),
      ...(Array.isArray(props.flup.message) ? {
        messageBlocks: props.flup.message as any
      } : {})
    }));
    setSimulating(false);
  }, [props.flup, props.task, props.enableEmailMarkdownGlobally])
  const debouncedSimulate = useDebouncedCallback(() => simulate(), 2000, [simulate]);

  useEffect(() => {
    if (liveUpdating === "simulation") {
      setSimulating(true);
      debouncedSimulate();
    }
  }, [JSON.stringify(props), props.enableEmailMarkdownGlobally, liveUpdating])

  return <div className="border border-gray-300 p-2 bg-gray-100 space-y-2">
    <div className="flex space-x-2">
      <b>Followup:&nbsp;{props.flup.suffix}</b>
      <DistroExprLink name={props.flup.suffix} expr={props.flup.send_if} />
    </div>
    <EnableKey component={{ ...props.flup, kind: 'Notification Followup', targetPrefix: props.task.targetPrefix }} parentComponent={props.task as HasEnableKey} />
    <ul className="flex space-x-1">
      <li>
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            if (liveUpdating === "eligibility") {
              setLiveUpdating(null);
              setFullQueryResult(null);
            } else {
              setSelectedTimestamp(new Date().toISOString());
              setLiveUpdating("eligibility");
            }
          }}
          loading={runningFullQuery}
        >
          {runningFullQuery ? 'Loading...' : liveUpdating === "eligibility" ? 'Watching for changes...' : 'Check Eligibility'}
        </Button>
      </li>
      <li>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setLiveUpdating(liveUpdating === "simulation" ? null : "simulation")}
          loading={simulating}
        >
          {simulating ? 'Simulating...' : liveUpdating === "simulation" ? 'Watching for changes...' : 'Simulate Formatted Notification'}
        </Button>
      </li>
    </ul>
    {liveUpdating === "eligibility" && (isCustomSQL ? (
      <p className="text-sm text-gray-600">Simulation date isn't supported for custom SQL queries.</p>
    ) : (
      <DateTimeInput
        label="Simulation Date"
        showLabel={false}
        value={selectedTimestamp}
        onChange={(isoString) => setSelectedTimestamp(isoString ?? undefined)}
        timezone={selectedTimezone}
        onTimezoneChange={setSelectedTimezone}
        setToNowLabel="Reset to Now"
      />
    ))}
    {props.task.recipient === 'Unsubmitted Applicant' && !(['email', 'sms', 'whatsapp'].includes(props.task.contactMethod)) &&
      <select className="ml-2 inline-flex items-center shadow-sm overflow-hidden bg-white px-2.5 py-1.5 text-black text-xs font-medium text-base text-gray-700 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded"
        onChange={(e) => setTestContactMethod(e.target.value)}>
        <option value="sms">SMS</option>
        <option value="email">Email</option>
        <option value="whatsapp">WhatsApp</option>
      </select>
    }
    <NotificationSimulation
      task={props.task}
      enableEmailMarkdown={props.task.enableEmailMarkdown || props.enableEmailMarkdownGlobally} simulated={simulated}
      simulationSource={liveUpdating} />
    {/* Full Query Results */}
    {fullQueryError && (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
        {fullQueryError}
      </div>
    )}
    {fullQueryResult && (
      <div className="border border-gray-200 rounded p-3 space-y-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm">Eligibility Results</h4>
          <Button variant="secondary" size="sm" onClick={() => setShowDownloadModal(true)}>
            Download
          </Button>
        </div>
        <div className="space-y-2">
          {(['email', 'sms', 'whatsapp'] as const).map(method => {
            const queryInfo = fullQueryResult.queries?.[method];
            if (!queryInfo) return null;
            const eligibleCount = fullQueryResult.eligible.filter(r => r.channel === method).length;
            return (
              <div key={method} className={`p-2 rounded border ${queryInfo.error ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium capitalize">{method}</span>
                    <span className="text-gray-600 ml-2">{eligibleCount} eligible</span>
                  </div>
                </div>
                {queryInfo.error && (
                  <div className="text-red-600 text-xs mt-1">{queryInfo.error}</div>
                )}
                <SqlViewer sql={queryInfo.query} />
              </div>
            );
          })}
        </div>
        {fullQueryResult.uniqueFailedCount > 0 &&
          <Alert variant="warning" iconName="ExclamationCircleIcon" >
            {fullQueryResult.uniqueFailedCount} notification{multipleFailed ? 's are' : ' is'} blocked due to an error.
            Please address {multipleFailed ? 'them' : 'it'} manually.
          </Alert>
        }
        <RecipientsTable
          eligible={fullQueryResult.eligible}
          sent={fullQueryResult.sent}
          variables={fullQueryResult.variables}
          recipientsTab={recipientsTab}
          setRecipientsTab={setRecipientsTab}
        />
      </div>
    )}
    {/* Download Modal */}
    {fullQueryResult && (
      <NotificationRecipientsDownloadModal
        notificationName={`${props.task.name}_${props.flup.suffix}`}
        sentData={fullQueryResult.sent}
        eligibleData={fullQueryResult.eligible}
        variables={fullQueryResult.variables}
        downloadModalOpen={showDownloadModal}
        setDownloadModalOpen={setShowDownloadModal}
      />
    )}
  </div>
}
